import {Component} from "@angular/core";
import {DynamicComponentBuilder} from "../renderer/dynamic-component-builder.service";
import {FormComponent} from "../renderer/model/form-component";
import {
    setOption,
    generateId,
    getOptionByKey,
    isTrue,
    setOptionRecursively, removeOptionRecursively
} from "../renderer/model/form-component-utils";
import {
    OptionsEnum, PREDEFINED_OPTIONS,
    SESSION_COMPONENT,
    SESSION_EDITOR_ON
} from "../renderer/model/dictionary";
import {MatDialog} from "@angular/material/dialog";
import {AbstractEditorComponent} from "./abstract.editor.component";
import {COMPONENT_TYPES_REGISTRY} from "../renderer/model/registry";

@Component({
    selector: "app-editor",
    templateUrl: './app.editor.component.html',
    styleUrls: ['./app.editor.component.css'],
})
export class AppEditorComponent extends AbstractEditorComponent {

    readonly componentTypeKeys = Array.from( COMPONENT_TYPES_REGISTRY.keys() );

    isWYSIWYGEditorOn = false;
    isTreeEditorOn = false;
    isSettingsOn = false;
    isBordersOn = true;
    isButtonsOn = true;

    // for settings:
    appearance: string;
    isFitScreen: boolean;
    noOfColumns: string;
    gridGap: 5;

    constructor(public dynamicFormsBuilderService: DynamicComponentBuilder, public dialog: MatDialog) {
        super(dynamicFormsBuilderService, dialog);
    }

    ngOnInit() {
        super.ngOnInit();

        const isEditorOn = isTrue(sessionStorage.getItem(SESSION_EDITOR_ON));
        if (isEditorOn !== undefined && isEditorOn != this.isWYSIWYGEditorOn) {
            this.toggleWYSIWYGEditor();
        }
        const defaultValueAppearance = PREDEFINED_OPTIONS.find(opt => opt.key === OptionsEnum.appearance).value;
        this.appearance = getOptionByKey(this.component, OptionsEnum.appearance, defaultValueAppearance);
        this.isFitScreen = isTrue(getOptionByKey(this.component, OptionsEnum.isFitScreen, undefined));
        const defaultValueGridCols = PREDEFINED_OPTIONS.find(opt => opt.key === OptionsEnum.gridCols).value;
        this.noOfColumns = getOptionByKey(this.component, OptionsEnum.gridCols, defaultValueGridCols);
        const defaultValueGridGap = PREDEFINED_OPTIONS.find(opt => opt.key === OptionsEnum.gridGap).value;
        this.gridGap = getOptionByKey(this.component, OptionsEnum.gridGap, defaultValueGridGap);
        this.publishChanges();
    }

    toggleBorders(): void {
        this.isBordersOn = !this.isBordersOn;
        console.log('Toggle borders:' + this.isBordersOn);
        const divs = document.getElementsByClassName('componentWrapper');
        for (let i=0 ; i < divs.length; i++) {
            const div: HTMLDivElement = <HTMLDivElement>divs.item(i);
            if (this.isBordersOn && this.isWYSIWYGEditorOn) {
                div.style.border = '1px dotted grey';
            } else {
                div.style.border = '1px dotted white';
            }
        }
    }

    toggleTreeEditor() {
        this.isTreeEditorOn = !this.isTreeEditorOn;
        console.log('Toggle Tree Editor: ' + this.isTreeEditorOn);
    }
    toggleSettings(): void {
        this.isSettingsOn = !this.isSettingsOn;
        console.log('Toggle isSettingsOn: ' + this.isSettingsOn);
    }
    toggleWYSIWYGEditor(): void {
        this.isWYSIWYGEditorOn = !this.isWYSIWYGEditorOn;
        console.log('Toggle WYSIWYG Editor:' + this.isWYSIWYGEditorOn);

        this.toggleButtons();
        this.toggleBorders();
        this.toggleTreeEditor();
        this.toggleSettings();
        sessionStorage.setItem(SESSION_EDITOR_ON, '' + this.isWYSIWYGEditorOn);
    }

    toggleButtons(): void {
        this.isButtonsOn = !this.isButtonsOn;
        console.log('Toggle buttons:' + this.isButtonsOn);
        const divs = document.querySelectorAll('.componentWrapper');
        divs.forEach(div => {
            if (this.isButtonsOn && this.isWYSIWYGEditorOn) {
                div.appendChild(this.createEditorButtons(div));
            } else {
                if (div.lastElementChild.id === 'editorButtons') {
                    div.removeChild(div.lastElementChild);
                }
            }
        })
    }

    restoreSample(): void {
        sessionStorage.removeItem(SESSION_COMPONENT);
        window.location.reload();
    }

    private createEditorButtons(parent: Element): HTMLElement {
        const newDiv = document.createElement("button");
        newDiv.id = 'editorButtons'; //for precaution
        newDiv.className = 'w3-dropdown-hover';
        newDiv.className = newDiv.className + ' mat-icon notranslate material-icons mat-icon-no-color';
        newDiv.innerText = 'more_horiz';
        newDiv.style.left = (parent.clientLeft) + 'px';
        newDiv.style.position = 'absolute';
        newDiv.style.top = '0px';
        newDiv.style.width = '32px';
        newDiv.style.height = '28px';
        newDiv.style.margin = '0px';
        newDiv.style.padding = '0px';
        newDiv.style.zIndex = '1000';

        const buttons = document.createElement("div");
        buttons.className = 'w3-dropdown-content w3-border';
        //buttons.style.zIndex = '1000';
        buttons.appendChild(this.createActionButton(parent.id, 'edit'));
        buttons.appendChild(this.createActionButton(parent.id, 'delete'));
        buttons.appendChild(this.createActionButton(parent.id, 'arrow_circle_up'));
        buttons.appendChild(this.createActionButton(parent.id, 'arrow_circle_down'));
        newDiv.appendChild(buttons);
        return newDiv;
    }

    private createActionButton(id: string, iconName: string): HTMLElement {
        const button = document.createElement("button");
        button.onclick = (ev) => {
            console.log(iconName + ' ' + id);
            switch(iconName) {
                case 'edit':
                    this.edit(id);
                    break;
                case 'delete':
                    this.delete(id);
                    break;
                case 'arrow_circle_up':
                    this.goUp(id);
                    break;
                case 'arrow_circle_down':
                    this.goDown(id);
                    break;
                default:
            }
        };
        button.style.display = 'block';
        button.style.zIndex = '1000';
        const icon = document.createElement("mat-icon");
        icon.innerText = iconName;
        icon.className = 'mat-icon notranslate material-icons mat-icon-no-color';
        button.appendChild(icon);
        return button;
    }

    getIcon(compType: string): string {
        return COMPONENT_TYPES_REGISTRY.get(compType).icon;
    }

    add(type: string) {
        const newComponent = new FormComponent();
        newComponent.type = type;
        newComponent.label = 'Just added';
        newComponent.id = generateId(newComponent);
        this.component.components.push(newComponent);
        this.reload();
    }

    setStretchToFit(): void {
        console.log('isFitScreen: ' + this.isFitScreen);
        setOptionRecursively(this.component, OptionsEnum.isFitScreen.toString(), !this.isFitScreen);
        this.reload();
    }

    setAppearance() {
        console.log('appearance: ' + this.appearance);
        setOptionRecursively(this.component, OptionsEnum.appearance.toString(), this.appearance);
        this.reload();
    }

    setNoOfColumns() {
        console.log('setNoOfColumns: ' + this.noOfColumns);
        setOption(this.component, OptionsEnum.gridCols.toString(), this.noOfColumns);
        this.reload();
    }

    setGridGap() {
        console.log('setGridGap: ' + this.gridGap);
        setOption(this.component, OptionsEnum.gridGap.toString(), this.gridGap);
        this.reload();
    }

    resetColspan(): void {
        console.log('resetColspan');
        removeOptionRecursively(this.component, OptionsEnum.colspan.toString());
        this.reload();
    }
}
